import type { ChatCreateSchema } from '@repo/schemas/chat/chatCreate';
import type { ChatEditSchema } from '@repo/schemas/chat/chatEdit';
import type { ChatPrivate, ConversationPublic } from '@repo/types/chat';
import type { MessageConfirmation } from '@repo/types/message';
import type { PaginationWithName } from '@repo/types/pagination';
import type { UserPublic } from '@repo/types/users';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class ChatService {
  static async getChat(id: number): Promise<AxiosResponse<ChatPrivate>> {
    return await axiosInstance.get(`/api/chat/${id}`);
  }

  static async getCreateConversationUsers(params: PaginationWithName): Promise<AxiosResponse<UserPublic[]>> {
    return await axiosInstance.get('/api/chat/getCreateConversationUsers', {
      params: params,
    });
  }

  static async getLastConversations(params: LastConversationsParams): Promise<AxiosResponse<Array<ConversationPublic>>> {
    return await axiosInstance.get('/api/chat/getLastConversations', {
      params: params,
    });
  }

  static async createChat(data: ChatCreateSchema): Promise<AxiosResponse<ChatPrivate>> {
    return await axiosInstance.post('/api/chat', data);
  }

  static async uploadChatFile(data: PostChatFileUploadData): Promise<AxiosResponse<MessageConfirmation>> {
    const { file, content, id, tempUUID, replyingToId, socketId, type } = data;

    const form = new FormData();
    if (content) {
      form.append('content', content);
    }
    if (replyingToId) {
      form.append('replyingToId', String(replyingToId));
    }
    if (socketId) {
      form.append('socketId', socketId);
    }
    form.append('file', file);
    form.append('tempUUID', tempUUID);
    form.append('type', type);

    return await axiosInstance.post(`/api/chat/${id}/fileUpload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static async editChat(data: ChatEditSchema): Promise<AxiosResponse<ChatPrivate>> {
    const { id, ...rest } = data;
    return await axiosInstance.put(`/api/chat/${id}`, rest);
  }

  static async deleteChat(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/chat/${id}`);
  }
}

export interface PostChatFileUploadData {
  file: File;
  content?: string;
  id: number;
  tempUUID: string;
  replyingToId?: number;
  type: string;
  socketId?: string;
}

export type ConversationsListFilter = 'individual' | 'mixed' | 'group' | undefined;

export interface LastConversationsParams extends PaginationWithName {
  type?: ConversationsListFilter;
}

export interface GetCreateConversationUsersInfiniteQueryProps {
  name?: string;
  enabled?: boolean;
}

export interface GetLastConversationsInfiniteQueryProps {
  name?: string;
  type?: ConversationsListFilter;
}
